import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AiOutlineLoading3Quarters, AiOutlineClose } from 'react-icons/ai';
import Dropdown from './Dropdown';
import AnswerDisplay from './AnswerDisplay';
import { API_URL } from '../creds';
import { toast } from 'react-toastify';
// import { sampleQuestionsTable } from '../utils/sampleQuestions';

const QuestionInput = () => {
  const [selectedVideo, setSelectedVideo] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [chunks, setChunks] = useState([]);
  const [selectedChunk, setSelectedChunk] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sampleQuestions, setSampleQuestions] = useState([]);
  const [activeQid, setActiveQid] = useState([]);
  // eslint-disable-next-line
  const [undoStack, setUndoStack] = useState([]);
  // eslint-disable-next-line
  const [redoStack, setRedoStack] = useState([]);

  //  Track question changes for undo history
  useEffect(() => {
    if (question !== '') {
      setUndoStack((prev) => [...prev, question]);
      setRedoStack([]);
    }
  }, [question]);

 
  // Fetch questions when dropdown (selectedVideo) changes
  useEffect(() => {
    if (!selectedVideo) {
      setSampleQuestions([]);
      return;
    }

    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${API_URL}/questions/${selectedVideo}`);
        const data = response.data;
        if (data.status === "success") {
          const questionList = data.questions.map((q) => q.question);
          setSampleQuestions(questionList);
        } else {
          toast.info(`No sample questions found.`);
          setSampleQuestions([]);
        }
      } catch (err) {
        toast.error(`No sample questions found..`);
        setSampleQuestions([]);
      }
    };

    fetchQuestions();
  }, [selectedVideo]);


  const handleSubmit = async () => {
    if (!selectedVideo || !question) {
      toast.error('Please select a dropdown option and enter a question!');
      return;
    }

    setLoading(true);
    setAnswer('');
    setChunks([]);
    setSelectedChunk(null);

    try {
      const response = await axios.post(`${API_URL}/ask`, {
        question,
        projectid: selectedVideo,
      });
      const data = response.data;
      setAnswer(data.answer);
      setChunks(data.chunks);
      toast.success('Answer Ready!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process your request.');
      setAnswer('Error fetching answer.');
    }

    setLoading(false);
  };

  // Clear + push to undo stack
  const handleClearQuestion = () => {
    if (question) {
      setUndoStack((prev) => [...prev, question]);
      setQuestion('');
      toast.info('Question cleared. Press Ctrl+Z to undo.');
    }
  };

  const handleUndoRedo = useCallback(
    (e) => {
      const isUndo = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z';
      const isRedo = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y';

      if (isUndo) {
        e.preventDefault();
        setUndoStack((prevUndo) => {
          if (prevUndo.length === 0) return prevUndo;
          const newUndo = [...prevUndo];
          const last = newUndo.pop();
          setRedoStack((prevRedo) => [...prevRedo, question]);
          setQuestion(last);
          return newUndo;
        });
      }

      if (isRedo) {
        e.preventDefault();
        setRedoStack((prevRedo) => {
          if (prevRedo.length === 0) return prevRedo;
          const newRedo = [...prevRedo];
          const redoValue = newRedo.pop();
          setUndoStack((prevUndo) => [...prevUndo, question]);
          setQuestion(redoValue);
          return newRedo;
        });
      }
    },
    [question]
  );


  // Attach key listener for Ctrl+Z / Ctrl+Y
  useEffect(() => {
    window.addEventListener('keydown', handleUndoRedo);
    return () => window.removeEventListener('keydown', handleUndoRedo);
  }, [handleUndoRedo]);

  return (
    <div className="max-w-3xl mx-auto p-2">
      <Dropdown selected={selectedVideo} setSelected={setSelectedVideo} />

      {/* Textarea */}
      <div className="mb-1 mt-2">
        <label className="block text-xl font-medium text-gray-900 mb-1">
          Ask Your Question / Sample Questions:
          {sampleQuestions.length > 0 && (
            <span className="ml-2">
              (
              {sampleQuestions.map((q, index) => (
                <button
                  key={index}
                  onClick={() => { setQuestion(q)
                    setActiveQid(index)
                  }}
                  className={`mx-0 px-1 py-0 rounded cursor-pointer transition-colors
                    ${
                      activeQid === index
                        ? "bg-blue-600 text-white font-semibold"
                        : "text-blue-700 hover:text-blue-900"
                    }`}
                  title={`question: ${q}`}
                >
                  {index + 1}
                </button>
              ))}
              )
            </span>
          )}
        </label>

        <div className="relative mb-0">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Write your question here..."
            className="w-full p-3 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-32 resize-none"
          ></textarea>

          {question && (
            <AiOutlineClose
              size={28}
              onClick={handleClearQuestion}
              className="absolute top-3 right-3 text-gray-500 hover:text-red-600 cursor-pointer"
              title="Clear (Ctrl+Z to undo)"
            />
          )}
        </div>
      </div>

      {/* Submit button */}
      <button
        onClick={handleSubmit}
        disabled={loading}
        className={`w-full py-2 rounded-md text-white transition-colors ${loading ? 'bg-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-900'
          }`}
      >
        {loading ? (
          <div className="flex justify-center items-center gap-2">
            <AiOutlineLoading3Quarters className="animate-spin text-xl" />
            Loading...
          </div>
        ) : (
          'Go'
        )}
      </button>

      <AnswerDisplay
        answer={answer}
        chunks={chunks}
        selectedChunk={selectedChunk}
        setSelectedChunk={setSelectedChunk}
      />
    </div>
  );
};

export default QuestionInput;
